import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useRef, useState } from 'react';

import Chevron from 'components/Chevron';
import ResponsiveImage from 'components/ResponsiveImage';
import { IMAGE_WIDTHS } from 'constants/images';
import { breakpoints, cn } from 'utils/Tailwind';
import '@splidejs/react-splide/css';

const SLIDES_PER_PAGE = {
  XS: 1,
  SM: 2,
  MD: 3,
  LG: 4,
  XL: 5,
  '2XL': 6,
};

const usePageSectionCardCarousel = items => {
  const [activeCarouselIndex, setActiveCarouselIndex] = useState(0);
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const currentCarousel = items[activeCarouselIndex];
  const carouselRef = useRef(null);

  const handleCategoryChange = useCallback(index => {
    setActiveCarouselIndex(index);
    carouselRef.current?.splide?.go(0);
  }, []);

  const handleOnMove = useCallback(({ index }) => {
    setActiveSlideIndex(index);
  }, []);

  return {
    activeSlideIndex,
    activeCarouselIndex,
    carouselRef,
    currentCarousel,
    handleCategoryChange,
    handleOnMove,
  };
};

const PageSectionCardCarousel = ({
  className,
  items,
  title,
  trackCTAClick,
  dataTestId,
  pdpStorytellingEnabled,
}) => {
  const {
    activeSlideIndex,
    activeCarouselIndex,
    carouselRef,
    currentCarousel,
    handleCategoryChange,
    handleOnMove,
  } = usePageSectionCardCarousel(items);

  const CAROUSEL_OPTIONS = useMemo(
    () => ({
      type: 'slide',
      pagination: false,
      gap: '0.5rem',
      mediaQuery: 'min',
      breakpoints: {
        0: {
          perPage: SLIDES_PER_PAGE.XS,
          padding: {
            left: pdpStorytellingEnabled ? '1.5rem' : '1rem',
            right: '30vw',
          },
        },
        [breakpoints.sm]: {
          perPage: SLIDES_PER_PAGE.SM,
          padding: {
            left: '1rem',
            right: '1rem',
          },
        },
        [breakpoints.md]: {
          perPage: SLIDES_PER_PAGE.MD,
          padding: {
            left: '2.5rem',
            right: '2.5rem',
          },
        },
        [breakpoints.lg]: {
          perPage: SLIDES_PER_PAGE.LG,
        },
        [breakpoints['3xl']]: {
          perPage: SLIDES_PER_PAGE['2XL'],
        },
      },
    }),
    [pdpStorytellingEnabled]
  );

  return (
    <section
      className={cn('CardCarousel relative my-6 flex flex-col md:my-10', className)}
      {...(dataTestId && { 'data-testid': dataTestId })}
    >
      <CarouselTabs
        activeCarouselIndex={activeCarouselIndex}
        handleCategoryChange={handleCategoryChange}
        items={items}
        title={title}
        pdpStorytellingEnabled={pdpStorytellingEnabled}
      />

      <Splide
        data-testid="splide"
        hasTrack={false}
        options={CAROUSEL_OPTIONS}
        ref={carouselRef}
        onMove={handleOnMove}
        key={currentCarousel.internalName}
      >
        <CarouselArrows activeSlideIndex={activeSlideIndex} items={currentCarousel.items} />
        <SplideTrack>
          {currentCarousel._contentType === 'cardCarouselCollection' && (
            <CollectionCards
              key={currentCarousel.internalName}
              items={currentCarousel.items}
              trackCTAClick={trackCTAClick}
            />
          )}
          {currentCarousel._contentType === 'cardCarouselColorway' && (
            <ColorwayCards
              key={currentCarousel.internalName}
              items={currentCarousel.items}
              trackCTAClick={trackCTAClick}
            />
          )}
        </SplideTrack>
      </Splide>
    </section>
  );
};

const CarouselTabs = ({
  activeCarouselIndex,
  handleCategoryChange,
  items,
  title,
  pdpStorytellingEnabled,
}) => {
  return (
    <div
      className={cn('mb-4 flex items-center justify-between px-4 md:mb-8 md:px-10', {
        'px-6 ': pdpStorytellingEnabled,
      })}
    >
      <h2 className="font-bold uppercase tracking-wide">{title}</h2>
      <nav className="inline-flex gap-4">
        {items.length > 1 &&
          items.map((carousel, index) => (
            <button
              key={carousel.title}
              className={cn(
                `transition-border pb-.5 whitespace-nowrap border-b-2 border-transparent font-bold uppercase tracking-wider duration-200 ease-in-out md:hover:border-black md:hover:text-black`,
                {
                  'pointer-events-none border-black text-black': activeCarouselIndex === index,
                  'text-gray-600': activeCarouselIndex !== index,
                }
              )}
              onClick={() => handleCategoryChange(index)}
            >
              {carousel.title}
            </button>
          ))}
      </nav>
    </div>
  );
};

const CarouselArrows = ({ activeSlideIndex, items }) => {
  return (
    <div className="splide__arrows hidden md:block">
      <button
        className={cn(`splide__arrow splide__arrow--prev !left-4 bg-white`, {
          '!hidden': !activeSlideIndex,
        })}
      >
        <Chevron direction="left" size={9} />
      </button>
      <button
        className={cn(`splide__arrow splide__arrow--next !right-4 bg-white`, {
          '!hidden': activeSlideIndex >= items.length - SLIDES_PER_PAGE.DESKTOP,
        })}
      >
        <Chevron direction="right" size={9} />
      </button>
    </div>
  );
};

const CollectionCards = ({ items, trackCTAClick }) => {
  return items.map(({ asset, handle, title }) => {
    const fullPath = `/collections/${handle}`;
    return (
      <SplideSlide key={`${title}-${handle}`}>
        <a
          className="group/card relative flex h-full flex-col"
          href={fullPath}
          onClick={() => trackCTAClick({ url: fullPath, text: title })}
        >
          <div className="overflow-hidden">
            <ResponsiveImage
              alt={asset.description}
              className="w-full bg-grey transition-transform duration-300 ease-in-out-expo md:group-hover/card:scale-105"
              src={asset.file.url}
              width={IMAGE_WIDTHS.W_600}
              height={IMAGE_WIDTHS.W_600}
              lazy
            />
          </div>

          <span className="mt-4 block overflow-hidden text-ellipsis whitespace-nowrap">
            <p className="text-md w-max font-bold">{title}</p>
          </span>
        </a>
      </SplideSlide>
    );
  });
};

const ColorwayCards = ({ items, trackCTAClick }) => {
  return items.map(
    ({ compareAtPrice, colorName, colorNameNoSole, handle, image, masterName, price }) => {
      const fullPath = `/products/${handle}`;
      return (
        <SplideSlide key={`${colorName}-${handle}`}>
          <a
            className="group/card relative flex h-full flex-col overflow-hidden"
            href={fullPath}
            onClick={() => trackCTAClick({ url: fullPath, text: `${masterName} - ${colorName}` })}
          >
            <div className="overflow-hidden">
              <ResponsiveImage
                alt={image.alt}
                className="w-full bg-grey transition-transform duration-300 ease-in-out-expo md:group-hover/card:scale-105"
                src={image.src}
                width={IMAGE_WIDTHS.W_600}
                height={IMAGE_WIDTHS.W_600}
                lazy
              />
            </div>

            <CardContent
              colorName={colorName}
              colorNameNoSole={colorNameNoSole}
              compareAtPrice={compareAtPrice}
              masterName={masterName}
              price={price}
            />
          </a>
        </SplideSlide>
      );
    }
  );
};

const CardContent = ({ compareAtPrice, colorName, colorNameNoSole, masterName, price }) => {
  return (
    <span className="mt-2 flex-grow gap-1 overflow-hidden text-ellipsis whitespace-nowrap md:gap-2">
      <p className="relative block w-max text-sm font-bold transition-opacity duration-200 ease-in-out xl:text-base">
        {masterName}
      </p>
      <p className="relative text-sm xl:text-base">{colorNameNoSole || colorName}</p>
      <p className="text-sm xl:text-base">
        <span className={cn('', [compareAtPrice && 'text-discount-red'])}>{price}</span>
        {compareAtPrice && <span className="ml-2 line-through">{compareAtPrice}</span>}
      </p>
    </span>
  );
};

PageSectionCardCarousel.propTypes = {
  className: PropTypes.string,
  dataTestId: PropTypes.string,
  internalName: PropTypes.string,
  title: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      items: PropTypes.arrayOf(PropTypes.shape({})),
    })
  ),
  pdpStorytellingEnabled: PropTypes.bool,
  trackCTAClick: PropTypes.func,
};

PageSectionCardCarousel.defaultProps = {
  items: [],
  title: '',
};

CarouselTabs.propTypes = {
  activeCarouselIndex: PropTypes.number,
  handleCategoryChange: PropTypes.func,
  items: PropTypes.array,
  title: PropTypes.string,
  pdpStorytellingEnabled: PropTypes.bool,
};

CarouselTabs.defaultProps = {
  activeCarouselIndex: 0,
  items: [],
  title: '',
};

CarouselArrows.propTypes = {
  activeSlideIndex: PropTypes.number,
  items: PropTypes.array,
};

CarouselArrows.defaultProps = {
  activeSlideIndex: 0,
};

ColorwayCards.propTypes = {
  isPlaceholderActive: PropTypes.any,
  items: PropTypes.array,
  trackCTAClick: PropTypes.func,
};

ColorwayCards.defaultProps = {
  isPlaceholderActive: null,
  items: [],
};

CollectionCards.propTypes = {
  items: PropTypes.array,
  trackCTAClick: PropTypes.func,
};

CollectionCards.defaultProps = {
  items: [],
};

CardContent.propTypes = {
  colorName: PropTypes.string,
  colorNameNoSole: PropTypes.string,
  compareAtPrice: PropTypes.string,
  handle: PropTypes.string,
  image: PropTypes.shape({
    src: PropTypes.string,
    alt: PropTypes.string,
  }),
  masterName: PropTypes.string,
  price: PropTypes.string,
};

CardContent.defaultProps = {
  colorName: '',
  colorNameNoSole: '',
  compareAtPrice: '',
  handle: '',
  image: {
    src: '',
    alt: '',
  },
  masterName: '',
  price: '',
};

export default PageSectionCardCarousel;
